<app-landing-navbar></app-landing-navbar>

<main class="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(25,151,180,0.18),_transparent_36%),linear-gradient(180deg,#f8fdff_0%,#eef7fb_42%,#ffffff_100%)] pt-[92px]">
  <section class="mx-auto flex max-w-6xl flex-col gap-8 px-4 pb-10 pt-6 sm:px-6 lg:px-8">
    <div class="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
      <div class="overflow-hidden rounded-[30px] border border-white/70 bg-[#062c3a] p-8 text-white shadow-[0_24px_80px_rgba(7,23,31,0.18)]">
        <div class="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[12px] font-semibold uppercase tracking-[0.18em] text-[#d8f6ff]">
          Public verification
        </div>

        <h1 class="mt-5 max-w-xl text-4xl font-black leading-tight tracking-[-0.04em] sm:text-5xl">
          Verify scanned certificates, payments, and demand notices.
        </h1>

        <p class="mt-4 max-w-xl text-base leading-7 text-slate-200">
          This page validates QR codes against the public verification endpoint and shows only the most important details for quick inspection.
        </p>

        <div class="mt-8 grid gap-4 sm:grid-cols-3">
          <div class="rounded-2xl border border-white/10 bg-white/8 p-4">
            <p class="text-xs uppercase tracking-[0.18em] text-slate-300">Type</p>
            <p class="mt-2 text-lg font-bold">{{ typeLabel }}</p>
          </div>
          <div class="rounded-2xl border border-white/10 bg-white/8 p-4">
            <p class="text-xs uppercase tracking-[0.18em] text-slate-300">QR value</p>
            <p class="mt-2 break-all text-sm font-semibold text-white/90">{{ normalizedValue || 'Waiting for QR value' }}</p>
          </div>
          <div class="rounded-2xl border border-white/10 bg-white/8 p-4">
            <p class="text-xs uppercase tracking-[0.18em] text-slate-300">Result</p>
            <p class="mt-2 text-lg font-bold">
              @if (state === 'loading') {
                Verifying...
              } @else if (state === 'found') {
                Verified
              } @else if (state === 'not_found') {
                Not found
              } @else if (state === 'error') {
                Needs attention
              } @else {
                Ready
              }
            </p>
          </div>
        </div>
      </div>

      <div class="rounded-[30px] border border-slate-200 bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)] sm:p-8">
        @if (state === 'loading') {
          <div class="flex min-h-[22rem] flex-col items-center justify-center text-center">
            <div class="h-14 w-14 animate-spin rounded-full border-4 border-slate-200 border-t-teal-600"></div>
            <p class="mt-4 text-sm font-medium text-slate-500">Verifying document...</p>
            <p class="mt-1 max-w-md text-sm text-slate-400">Please wait while we confirm the QR code value with the public verification service.</p>
          </div>
        } @else if (state === 'error') {
          <div class="flex min-h-[22rem] flex-col justify-center">
            <div class="inline-flex w-fit rounded-full bg-rose-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-rose-700">
              Verification issue
            </div>
            <h2 class="mt-4 text-2xl font-black text-slate-900">{{ title }}</h2>
            <p class="mt-3 text-sm leading-6 text-slate-600">{{ errorMessage }}</p>

            <div class="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              Make sure the QR code includes both a `type` and a `value`, then try again.
            </div>

            <div class="mt-6 flex flex-wrap gap-3">
              <a routerLink="/" class="rounded-xl bg-teal-600 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-teal-700">
                Back to home
              </a>
              <button
                type="button"
                (click)="retry()"
                class="rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
              >
                Retry
              </button>
            </div>
          </div>
        } @else if (state === 'not_found') {
          <div class="flex min-h-[22rem] flex-col justify-center">
            <div class="inline-flex w-fit rounded-full bg-amber-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-amber-700">
              Not found
            </div>
            <h2 class="mt-4 text-2xl font-black text-slate-900">Document not found</h2>
            <p class="mt-3 text-sm leading-6 text-slate-600">
              We could not find a matching public record for this QR code.
            </p>

            <div class="mt-6 rounded-2xl border border-amber-100 bg-amber-50/60 p-4 text-sm text-amber-900">
              Check that the QR code value is complete and that the document type is correct.
            </div>

            <div class="mt-6 flex flex-wrap gap-3">
              <a routerLink="/" class="rounded-xl bg-teal-600 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-teal-700">
                Back to home
              </a>
              <button
                type="button"
                (click)="retry()"
                class="rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
              >
                Try again
              </button>
            </div>
          </div>
        } @else if (state === 'found') {
          <div class="flex items-center justify-between gap-4 border-b border-slate-100 pb-5">
            <div>
              <div class="inline-flex rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">
                {{ resultBadge }}
              </div>
              <h2 class="mt-3 text-2xl font-black text-slate-900">Verification successful</h2>
              <p class="mt-2 text-sm leading-6 text-slate-600">{{ resultSummary }}</p>
            </div>
            <div class="hidden rounded-2xl bg-emerald-50 px-4 py-3 text-right md:block">
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">Document type</p>
              <p class="mt-1 text-sm font-bold text-emerald-900">{{ typeLabel }}</p>
            </div>
          </div>

          <div class="mt-5 grid gap-3 sm:grid-cols-2">
            @for (row of resultRows; track row.label) {
              <div class="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p class="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">{{ row.label }}</p>
                <p
                  class="mt-2 break-words text-sm font-semibold text-slate-900"
                  [class.text-emerald-700]="row.tone === 'success'"
                  [class.text-amber-700]="row.tone === 'warning'"
                  [class.text-rose-700]="row.tone === 'danger'"
                >
                  {{ row.value }}
                </p>
              </div>
            }
          </div>

          <div class="mt-6 flex flex-wrap gap-3">
            <a routerLink="/" class="rounded-xl bg-teal-600 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-teal-700">
              Back to home
            </a>
            <button
              type="button"
              (click)="retry()"
              class="rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
            >
              Refresh
            </button>
          </div>
        } @else {
          <div class="flex min-h-[22rem] flex-col items-center justify-center text-center">
            <div class="h-14 w-14 rounded-full border border-teal-100 bg-teal-50"></div>
            <h2 class="mt-4 text-2xl font-black text-slate-900">Waiting for verification data</h2>
            <p class="mt-2 max-w-md text-sm leading-6 text-slate-600">
              Open this page with `?type=` and `?value=` query parameters to verify a scanned QR code.
            </p>
          </div>
        }
      </div>
    </div>
  </section>
</main>
